import { useCallback, useContext, useMemo, Children, useState } from 'react';
import { type Options as MarkdownOptions } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';
import { ChatHintType } from '@trinity/chat';
import {
  ActionsContext,
  ActiveChatSessionContext,
  AttachmentItemRendererContext,
} from '@trinity/chat-components/components/Chat/context';
import { useUpdateProfileMutation } from '@trinity/chat-components/components/Chat/KolSearch.Containers';
import { LearnMore } from '@trinity/chat-components/components/Chat/LearnMore';
import { PatientVolumeTooltip } from '@trinity/chat-components/components/presentation/PatientVolumeTooltip';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useHover } from '@trinity/chat-components/hooks/useHover';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { MessageQueryHint, TextQueryHint } from '../presentation/Message.QueryHint';
import { ArrowButton } from './ArrowButton';
import { Citation, Citations } from './Citations';
import { MarkdownBadge } from './MarkdownBadge';
import {
  StyledA,
  StyledBlockQuote,
  StyledH1,
  StyledH2,
  StyledH3,
  StyledH4,
  StyledLi,
  StyledMark,
  StyledOL,
  StyledP,
  StyledStrong,
  StyledTd,
  StyledTh,
  StyledTr,
  StyledUl,
  StyledTable,
} from './MarkdownElements';
import { MarkdownInfoChip } from './MarkdownInfoChip';
import { ScrollableTable } from './ScrollableTable';

const Spacer = () => <div style={{ height: '10px' }} />;

const CodeBlockWrapper = styled.div`
  position: relative;
  margin: 16px 0;
`;

const CopyButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8) !important;
  &:hover {
    background-color: rgba(255, 255, 255, 0.95) !important;
  }
`;

const CodeBlock = ({ language, children }: { language?: string; children: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <CodeBlockWrapper>
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <CopyButton onClick={handleCopy} size="small">
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </CopyButton>
      </Tooltip>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneLight}
        customStyle={{
          margin: 0,
          borderRadius: '6px',
          fontSize: '14px',
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            fontSize: '14px',
            lineHeight: '1.45',
          },
        }}
      >
        {children}
      </SyntaxHighlighter>
    </CodeBlockWrapper>
  );
};

export function useMarkdownComponents(
  components?: MarkdownOptions['components']
): MarkdownOptions['components'] {
  const AttachmentItem = useContext(AttachmentItemRendererContext);

  return useMemo(
    () => ({
      citations: (props) => {
        const citations = props.node.properties.values as string[];
        return <Citations values={citations} />;
      },
      td: (props) => {
        const content = Children.map(props.children, (c) => {
          if (c === '<br />') {
            return <br />;
          }
          return c;
        });
        return <StyledTd>{content}</StyledTd>;
      },
      p: (props) => {
        // If paragraph contains a block-level custom component, avoid div-in-p DOM nesting
        type HastChild = { type: string; tagName?: string };
        const nodeChildren = (props.node as { children?: HastChild[] })?.children ?? [];
        const hasBlockCustomComponent = nodeChildren.some(
          (child) => child.type === 'element' && child.tagName === 'customComponent'
        );
        if (hasBlockCustomComponent) {
          return <div>{props.children}</div>;
        }

        // Check if paragraph ends with "call plan."
        const text = typeof props.children === 'string' ? props.children : '';
        const hasCallPlan =
          text.includes('call plan.') ||
          (Array.isArray(props.children) &&
            props.children.some((child) => typeof child === 'string' && child.includes('call plan.')));

        if (hasCallPlan) {
          return (
            <>
              <StyledP>{props.children}</StyledP>
              <Spacer />
            </>
          );
        }
        return <StyledP>{props.children}</StyledP>;
      },
      blockquote: ({ children }) => <StyledBlockQuote>{children}</StyledBlockQuote>,
      a: (props) => (
        <StyledA href={props.href} target="_blank" rel="noopener">
          {props.children}
        </StyledA>
      ),
      ul: (props) => <StyledUl>{props.children}</StyledUl>,
      li: (props) => <StyledLi>{props.children}</StyledLi>,
      ol: (props) => <StyledOL>{props.children}</StyledOL>,
      strong: (props) => {
        // Check if this is "Contact History" or "Disclaimer" heading
        const text = typeof props.children === 'string' ? props.children : '';
        if (text === 'Contact History') {
          return (
            <>
              <StyledStrong>{props.children}</StyledStrong>
              <Spacer />
            </>
          );
        }
        if (text === 'Disclaimer') {
          return (
            <>
              <Spacer />
              <StyledStrong>{props.children}</StyledStrong>
            </>
          );
        }
        return <StyledStrong>{props.children}</StyledStrong>;
      },
      h1: (props) => <StyledH1>{props.children}</StyledH1>,
      h2: (props) => <StyledH2>{props.children}</StyledH2>,
      h3: (props) => <StyledH3>{props.children}</StyledH3>,
      h4: (props) => <StyledH4>{props.children}</StyledH4>,
      table: (props) => <ScrollableTable>{props.children}</ScrollableTable>,
      tr: (props) => <StyledTr>{props.children}</StyledTr>,
      th: (props) => <StyledTh>{props.children}</StyledTh>,
      summary: ({ title }) => {
        return <summary>{title}</summary>;
      },
      citation: (props) => {
        const citation = props.node.properties.value as string;
        return <Citation value={citation} />;
      },
      mark: (props) => <StyledMark>{props.children}</StyledMark>,
      pre: (props) => {
        // Return just the children - the code component will handle rendering
        return <>{props.children}</>;
      },
      code: (props) => {
        const { children, className, node, ...rest } = props;
        const isInline = !className;

        if (isInline) {
          return (
            <code
              style={{
                backgroundColor: 'rgba(175, 184, 193, 0.2)',
                borderRadius: '6px',
                padding: '0.2em 0.4em',
                fontSize: '85%',
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
              }}
            >
              {children}
            </code>
          );
        }

        // Extract language from className (e.g., "language-sql" -> "sql")
        const language = className?.replace('language-', '');
        const codeString = String(children).replace(/\n$/, '');

        return <CodeBlock language={language}>{codeString}</CodeBlock>;
      },
      customComponent: (props) => {
        const componentName = props.node.properties.value as string;
        switch (componentName) {
          case 'patient-volume-tooltip':
            return <PatientVolumeTooltip />;
          default:
            if (/^attachment:/.test(componentName) && AttachmentItem) {
              const [_, id] = componentName.split(':');
              return <AttachmentItem id={id} />;
            }

            return null;
        }
      },
      infoChip: ({ node, text, tooltipText, ...props }) => {
        return (
          <MarkdownInfoChip {...props} text={tooltipText} placement="bottom">
            {text}
          </MarkdownInfoChip>
        );
      },
      badge: ({ node, ...props }) => {
        return (
          <>
            <MarkdownBadge {...props} text={props.text} placement="bottom">
              {props.text}
            </MarkdownBadge>
            {!props.inline && <Spacer />}
          </>
        );
      },
      queryhint: ({ node, displayValue, inputValue, inline }) => {
        if (!inline) {
          return (
            <div style={{ marginBottom: 8 }}>
              <QueryHintMDN displayValue={displayValue} inputValue={inputValue} />
            </div>
          );
        } else {
          return <QueryHintInline displayValue={displayValue} inputValue={inputValue} />;
        }
      },
      viewprofile: ({ id, name, identifier, displayValue }) => {
        return <ViewProfile id={id} name={name} identifier={identifier} displayValue={displayValue} />;
      },
      arrowButton: ({ action }) => {
        const actions = useContext(ActionsContext);
        const onClick = useMemo(() => {
          return actions[action] || (() => {});
        }, [actions, action]);
        return <ArrowButton onClick={onClick} />;
      },
      ...components,
    }),
    [components, AttachmentItem]
  );
}

const QueryHintMDN = (props: { displayValue: string; inputValue: string }) => {
  const dispatch = useContext(ChatStateDispatchContext);

  const { displayValue, inputValue } = props;

  const handleHint = useCallback(() => {
    dispatch({
      type: 'input-changed',
      payload: { value: inputValue },
    });
  }, [dispatch, inputValue]);
  return (
    <MessageQueryHint
      hint={{
        type: ChatHintType.Text,
        displayValue,
      }}
      onClick={handleHint}
      disabled={false}
    />
  );
};

const QueryHintInline = (props: { displayValue: string; inputValue: string }) => {
  const [ref, isHovering] = useHover<HTMLLIElement>(!!props.inputValue);

  return (
    <span ref={ref}>
      <LearnMore open={isHovering} displayValue={props.displayValue} question={props.inputValue} />
    </span>
  );
};

type ViewProfileProps = {
  id: number;
  name: string;
  identifier: string;
  displayValue?: string;
};

export const ViewProfile = (props: ViewProfileProps) => {
  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const updateProfileMutation = useUpdateProfileMutation({
    identifier: props.identifier,
  });

  const apiClient = useChatApiClient();

  const handleClick = useCallback(async () => {
    const search = await apiClient.kolSearch.submitKolSearch({
      chatIdentifier: chat.identifier,
      identifier: props.identifier,
      form: {
        kolId: props.id,
        name: props.name,
      },
      recommendations: {
        status: 'loaded',
        items: [],
        total: 0,
      },
      brandIdentifier,
    });

    dispatch({
      type: 'kol-search-profile-available',
      payload: {
        chatInstance: search.chatInstance,
        result: search.result,
        identifier: props.identifier,
      },
    });

    await updateProfileMutation.mutateAsync({
      kolSearchId: search.result.id,
      kolId: search.result.results[0].id,
    });
  }, [
    chat.identifier,
    props.identifier,
    props.id,
    props.name,
    brandIdentifier,
    dispatch,
    updateProfileMutation,
  ]);

  return <TextQueryHint onClick={handleClick} value={props.displayValue} />;
};
